import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import feather from 'feather-icons';
import { getTransactionClass, getTransactionImage } from '../../util/transactionHelper';
import UpdateTransactionBottomSheet from '../Modals/UpdateTransactionBottomSheet';
import SuccessToast from '../../toast/SuccessToast';
import { amountDisplay, dateDisplay } from '../../util/helper';
import WarningToast from '../../pages/WarningToast';
import { useLanguage } from '../../context/LanguageContext';

// Skeleton Transaction Loader
const TransactionSkeleton = () => (
    <div className="col-12">
        <div className="transaction-box skeleton-transaction">
            <div style={{ display: 'flex', gap: '12px' }}>
                <div
                    className="skeleton-loading"
                    style={{ width: '48px', height: '48px', borderRadius: '8px' }}
                ></div>
                <div style={{ flex: 1 }}>
                    <div
                        className="skeleton-loading"
                        style={{ width: '60%', height: '14px', marginBottom: '8px' }}
                    ></div>
                    <div
                        className="skeleton-loading"
                        style={{ width: '80%', height: '12px' }}
                    ></div>
                </div>
            </div>
        </div>
    </div>
);

const TransactionList = ({ recentTransactions, refreshHome }) => {
    const { t, language } = useLanguage();

    useEffect(() => {
        feather.replace();
    }, []);

    const [transaction, setTransaction] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showToastForDelete, setShowToastForDelete] = useState(false);

    const handleShowBottomSheet = useCallback((selectedTransaction) => {
        setTransaction(null);
        setShowModal(false);

        setTimeout(() => {
            setTransaction(selectedTransaction);
            setShowModal(true);
        }, 10);
    }, []);

    const handleCloseBottomSheet = useCallback(() => {
        setShowModal(false);
    }, []);

    const handleTransactionAction = useCallback(() => {
        refreshHome();
    }, [refreshHome]);

    const getAccountDisplay = useCallback(
        (transaction) => {
            if (transaction.category_name === null) {
                const fromAccount =
                    transaction.account_id === 0 ? t('outOfWallet') : transaction.account_name;
                const toAccount =
                    transaction.to_account_id === 0
                        ? t('outOfWallet')
                        : transaction.to_account_name;
                return `${fromAccount} → ${toAccount}`;
            }
            return transaction.account_name;
        },
        [t]
    );

    // Show skeleton loading if recentTransactions is null or undefined
    const isLoading = !Array.isArray(recentTransactions);
    const isEmpty = Array.isArray(recentTransactions) && recentTransactions.length === 0;

    return (
        <>
            <section>
                <div className="custom-container">
                    <div className="title">
                        <h2>{t('latestTransaction')}</h2>
                        {!isEmpty && (
                            <Link to="/transaction-history" className="see-all-link">
                                {t('seeAll')}
                            </Link>
                        )}
                    </div>

                    {!isEmpty && (
                        <div className="title">
                            <h4 className="margin-bottom-10">{t('mostRecent')}</h4>
                        </div>
                    )}

                    <div className="row gy-3">
                        {isLoading ? (
                            // Show skeleton loaders while loading
                            Array.from({ length: 3 }).map((_, idx) => (
                                <TransactionSkeleton key={`skeleton-${idx}`} />
                            ))
                        ) : isEmpty ? (
                            // Empty State
                            <div className="col-12">
                                <div className="empty_state">
                                    <img
                                        className="img-fluid transaction-icon empty-transaction-icon"
                                        src={'/assets/images/svg/empty-v4.svg'}
                                        alt={t('noTransactions')}
                                    />
                                    <h3>{t('noTransactions')}</h3>
                                    <p>{t('noTransactionsFound')}</p>
                                </div>
                            </div>
                        ) : (
                            // Transaction List
                            recentTransactions.map((txn) => {
                                const localDateString = txn.transaction_time.replace('Z', '');
                                const bdDate = new Date(localDateString);
                                const formattedDate = bdDate.toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric',
                                });

                                return (
                                    <div className="col-12" key={txn.id}>
                                        <button
                                            className="transaction-box-btn"
                                            onClick={() => handleShowBottomSheet(txn)}
                                            aria-label={`${txn.category_name || t('transfer')} - ${amountDisplay(txn.amount, language)}`}
                                        >
                                            <div className="transaction-image">
                                                <img
                                                    className="img-fluid transaction-icon"
                                                    src={getTransactionImage(txn.category_id)}
                                                    alt={txn.transaction_type}
                                                    loading="lazy"
                                                />
                                            </div>
                                            <div className="transaction-details">
                                                <div className="transaction-name">
                                                    <h5>{txn.category_name ?? t('transfer')}</h5>
                                                    <h3
                                                        className={getTransactionClass(
                                                            txn.transaction_type
                                                        )}
                                                    >
                                                        {amountDisplay(txn.amount, language)}
                                                    </h3>
                                                </div>
                                                <div className="transaction-meta">
                                                    <h5 className="light-text transaction-account">
                                                        {getAccountDisplay(txn)}
                                                    </h5>
                                                    <h5 className="light-text transaction-date">
                                                        {dateDisplay(formattedDate, language)}
                                                    </h5>
                                                </div>
                                            </div>
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </section>

            {transaction && showModal && (
                <UpdateTransactionBottomSheet
                    show={showModal}
                    onClose={handleCloseBottomSheet}
                    onTransactionUpdate={handleTransactionAction}
                    setShowToast={setShowToast}
                    transaction={transaction}
                    setShowToastForDelete={setShowToastForDelete}
                />
            )}

            {showToast && (
                <SuccessToast
                    message={t('transactionUpdated')}
                    onClose={() => setShowToast(false)}
                />
            )}

            {showToastForDelete && (
                <WarningToast
                    icon={'https://cdn-icons-png.flaticon.com/512/6861/6861362.png'}
                    message={t('transactionRemoved')}
                    onClose={() => setShowToastForDelete(false)}
                />
            )}
        </>
    );
};

export default TransactionList;
